/** 成长记录的照片：显示（签名地址）、添加（选好就上传，最多 9 张）、删除 */
import { ImagePlus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { assetUrl, didWrite, toast, upload } from "../../core";
import { Spinner } from "../../ui";

export const MAX_CARD_PHOTOS = 9;

/** 一张附件照片（方形裁切）；onClick 时可以点开 */
export function CardPhoto({ assetId, onClick, size }: { assetId: string; onClick?: () => void; size?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    assetUrl(assetId)
      .then((u) => alive && setUrl(u))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [assetId]);
  const style = size ? { width: size, height: size } : undefined;
  const img = url ? <img src={url} alt="" loading="lazy" /> : <span className="kp-card-photo-ph" />;
  return onClick ? (
    <button type="button" className="kp-card-photo" style={style} onClick={onClick} aria-label="看大图">
      {img}
    </button>
  ) : (
    <span className="kp-card-photo" style={style}>
      {img}
    </span>
  );
}

/** 生成的卡面图（整张卡，带金边，透明背景，3:4，docs/卡面生成.md），完整显示不裁切 */
export function CardArtImage({ assetId }: { assetId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    assetUrl(assetId)
      .then((u) => alive && setUrl(u))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [assetId]);
  return url ? <img className="kp-card-art-img" src={url} alt="" loading="lazy" /> : <span className="kp-card-art-img" />;
}

/** 照片编辑：缩略图 + 删除 + 添加 */
export function CardPhotosEditor({ value, onChange, onUploading, onView }: { value: string[]; onChange: (ids: string[]) => void; onUploading?: (b: boolean) => void; onView?: (id: string) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const latest = useRef(value);
  latest.current = value;

  const add = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    onUploading?.(true);
    try {
      for (const f of Array.from(files)) {
        if (latest.current.length >= MAX_CARD_PHOTOS) break;
        if (!f.type.startsWith("image/")) continue;
        try {
          const asset = await upload(f);
          didWrite("assets", asset);
          latest.current = [...latest.current, asset.id];
          onChange(latest.current);
        } catch (e) {
          toast(`照片上传失败：${e instanceof Error ? e.message : String(e)}`, { error: true });
        }
      }
    } finally {
      setUploading(false);
      onUploading?.(false);
      if (input.current) input.current.value = "";
    }
  };

  return (
    <div className="kp-card-photos-editor">
      <div className="kp-card-photos-head">
        <span>照片</span>
        <small className="kp-num">
          {value.length}/{MAX_CARD_PHOTOS}
        </small>
      </div>
      <div className="kp-card-photos-grid">
        {value.map((id) => (
          <div key={id} className="kp-card-photo-cell">
            <CardPhoto assetId={id} onClick={onView ? () => onView(id) : undefined} />
            <button type="button" className="kp-card-photo-del" aria-label="删除照片" onClick={() => onChange(value.filter((x) => x !== id))}>
              <X size={13} strokeWidth={3} />
            </button>
          </div>
        ))}
        {value.length < MAX_CARD_PHOTOS && (
          <button type="button" className="kp-card-photo-add" disabled={uploading} onClick={() => input.current?.click()} data-testid="add-photo">
            {uploading ? <Spinner size={18} /> : <ImagePlus size={20} strokeWidth={2.2} />}
            <span>{uploading ? "上传中" : "加照片"}</span>
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => void add(e.target.files)} />
    </div>
  );
}
